import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Users, Briefcase, TrendingUp, Star, CheckCircle, Network, Cable, Shield, Video, Package, VideoIcon, Award, Target, Zap, Clock, FileText, Rocket, X, Play, ChevronRight, Sparkles, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, Navigation, EffectFade } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import 'swiper/css/effect-fade';
import demoVideo from '../data/vid/video1.mp4';

const Home = () => {
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [animatedStats, setAnimatedStats] = useState({});

  const heroSlides = [
    {
      title: "Hire Proven Experts You Can Rely On",
      subtitle: "A commitment to providing seasoned IT professionals who bring experience, precision, and confidence to every project ",
      image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?q=80&w=2070",
      cta: "Talk to Our Team",
      highlight: "50 + Tech Professionals"
    },
    {
      title: "Professional Who Deliver Dependable Results",
      subtitle: "A commitment to providing seasoned IT professionals who bring experience, precision, and confidence to every project ",
      image: "https://images.unsplash.com/photo-1573164713988-8665fc963095?q=80&w=2069",
      cta: "Explore Our Services",
      highlight: "Begin Your IT Consultation"
    },
    {
      title: "Empowering Your Business Through IT Partner You Can Trust ",
      subtitle: "A reassurance that your organization gains a long‑term, reliable partner dedicated to strengthening operations and supporting growth through dependable IT support. ",
      image: "https://images.unsplash.com/photo-1551434678-e076c223a692?q=80&w=2070",
      cta: "Connect With Us",
      highlight: "Client Satisfaction: High"
    }
  ];

  const stats = [
    { icon: Briefcase, value: 20, suffix: '+', label: 'Tech Professionals', color: 'from-blue-500 to-cyan-500', description: 'A seasoned team equipped to handle complex IT needs with confidence' },
    { icon: Award, value: null, suffix: 'High', label: 'Client Satisfaction', color: 'from-purple-500 to-pink-500', description: 'Clients consistently value our reliability, responsiveness, and professionalism.' },
    { icon: CheckCircle, value: null, suffix: 'Excellent', label: 'Success Rate', color: 'from-indigo-500 to-violet-500', description: 'Projects are delivered with precision, consistency, and dependable outcomes.' },
    { icon: Star, value: null, suffix: 'Exceptional', label: 'Support Quality', color: 'from-orange-500 to-yellow-500', description: 'If you want them even shorter or more premium-sounding, I can refine them further.' },
  ];

  const services = [
    {
      icon: Network,
      title: 'Data Center Migration',
      description: 'End-to-end data center migrations: planning, compatibility analysis, staged rollout, policy mapping, and post-migration optimization.',
      highlights: ['Migration Planning', 'Staged Rollout', 'Post-Migration Support'],
      slug: 'broadcom-migration',
      stats: { timeline: '4–8 weeks', complexity: 'Enterprise' },
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Cable,
      title: 'Office Recabling',
      description: 'Structured cabling: Cat6/Cat6a/Cat7 design and install, cable management, labeling, testing and certification.',
      highlights: ['Cat6/7 Installation', 'Cable Management', 'Testing & Certification'],
      slug: 'office-recabling',
      stats: { timeline: '2–6 weeks', complexity: 'Medium' },
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: Shield,
      title: 'Switch & Firewall',
      description: 'Supply, install and configure network switches and enterprise firewalls — VLANs, ACLs, HA, VPNs, and security hardening.',
      highlights: ['VLAN Configuration', 'VPNs & HA', 'Security Hardening'],
      slug: 'switch-firewall',
      stats: { timeline: '3–5 weeks', complexity: 'High' },
      color: 'from-indigo-500 to-violet-500'
    },
    {
      icon: Video,
      title: 'Audio & Video Solutions',
      description: 'AV installs for meeting rooms, boardrooms and auditoriums — speakers, mixers, displays, amplifiers and control integration.',
      highlights: ['Room Assessment', 'AV Installation', 'Control Integration'],
      slug: 'audio-video-solutions',
      stats: { timeline: '1–4 weeks', complexity: 'Medium' },
      color: 'from-yellow-500 to-orange-400'
    },
    {
      icon: Package,
      title: 'IT Equipment Warehousing',
      description: 'Secure warehousing and inventory management for IT equipment — receiving, asset tagging, climate control and dispatch.',
      highlights: ['Asset Tagging', 'Climate Control', 'Dispatch Logistics'],
      slug: 'it-equipment-warehousing',
      stats: { timeline: 'Ongoing', complexity: 'Low' },
      color: 'from-slate-500 to-gray-500'
    },
    {
      icon: VideoIcon,
      title: 'Conferencing Solutions',
      description: 'Full conferencing replacements and installs: Zoom/Teams room setups, cameras, mics, codec integration and room scheduling.',
      highlights: ['Zoom/Teams Rooms', 'Codec Integration', 'Room Scheduling'],
      slug: 'conferencing-solutions',
      stats: { timeline: '1–3 weeks', complexity: 'Medium' },
      color: 'from-cyan-500 to-blue-400'
    },
  ];

  const benefits = [
    { text: 'Core Strength', icon: Sparkles, description: 'A streamlined, agile team structure lets us move faster and deliver higher-quality service than larger, slower competitors.' },
    { text: 'Time Efficiency', icon: Clock, description: 'We respond quickly, eliminate delays, and ensure clients get the right expert exactly when they need one.' },
    { text: 'Thorough Technical Screening', icon: CheckCircle, description: 'Every candidate is carefully evaluated to confirm their skills, experience, and readiness to perform from day one.' },
    { text: 'People and Culture Fit', icon: Users, description: 'We prioritize professionals who not only excel technically but also align with your team’s values and working style.' },
    { text: 'Consistent, High-Standard Service', icon: Award, description: 'We maintain a strong commitment to quality, delivering dependable support that builds long-term trust.' },
    { text: 'Meeting Requirements with Precision', icon: Target, description: 'Your needs guide every step — from candidate selection to onboarding — ensuring a perfect match for your environment.' }
  ];

  const process = [
    {
      icon: FileText,
      title: 'Send Us Your Requirements',
      description: 'Your inquiry gives us the clarity to understand your technical needs and business goals. We assess your requirements promptly to ensure a precise and efficient match.'
    },
    {
      icon: Users,
      title: 'Candidate',
      description: 'Our team carefully selects professionals whose experience aligns directly with your technical challenges. You receive only candidates who meet your expectations and standards.'
    },
    {
      icon: Zap,
      title: 'Technical Screening',
      description: 'Each candidate undergoes a structured technical evaluation to confirm their capabilities. This step ensures you receive experts who can perform reliably from day one.'
    },
    {
      icon: Rocket,
      title: 'Smooth Onboarding Support',
      description: 'We help both you and the selected expert through a seamless onboarding process. Our support ensures a quick, hassle-free start so work can begin immediately.'
    }
  ];

  const industries = [
    { name: 'Finance', icon: Building2, clients: '150+', desc: 'Finance' },
    { name: 'Healthcare', icon: Award, clients: '60+', desc: 'Healthcare' },
    { name: 'Manufacturing', icon: Briefcase, clients: '80+', desc: 'Manufacturing' },
    { name: 'Logistics', icon: Target, clients: '45+', desc: 'Logistics' },
    { name: 'Technology', icon: TrendingUp, clients: '70+', desc: 'Technology' },
    { name: 'Government', icon: Shield, clients: '40+', desc: 'Government' },
  ];

  const testimonials = [
    {
      name: 'Kenji Watanabe',
      role: 'IT Director, Apex Holdings',
      image: 'https://randomuser.me/api/portraits/men/11.jpg',
      content: 'Tasks IT Services handled our entire data center migration with zero unplanned downtime. Their planning was meticulous and the team communicated proactively throughout.',
      rating: 5,
      company: 'Apex Holdings',
      service: 'Data Center Migration'
    },
    {
      name: 'Hiroko Tanaka',
      role: 'Operations Manager, MedCore Japan',
      image: 'https://randomuser.me/api/portraits/women/12.jpg',
      content: 'Our new office cabling and AV setup was completed ahead of schedule. The quality of work and professionalism of the on-site team was outstanding.',
      rating: 5,
      company: 'MedCore Japan',
      service: 'Office Recabling & AV'
    },
    {
      name: 'David Okafor',
      role: 'CTO, Finbridge Asia',
      image: 'https://randomuser.me/api/portraits/men/14.jpg',
      content: 'They configured our entire firewall and VLAN architecture from scratch. Security hardening was thorough and the documentation they delivered was exceptional.',
      rating: 5,
      company: 'Finbridge Asia',
      service: 'Switch & Firewall'
    },
    {
      name: 'Yuki Matsumoto',
      role: 'Facilities Lead, EduNet Corp',
      image: 'https://randomuser.me/api/portraits/women/15.jpg',
      content: 'Our Teams Room rollout across 12 meeting rooms went seamlessly. Staff were trained on day one and feedback has been overwhelmingly positive.',
      rating: 5,
      company: 'EduNet Corp',
      service: 'Conferencing Solutions'
    },
  ];

  const recentProjects = [
    { service: 'Data Center Migration', client: 'Apex Holdings', location: 'Tokyo', duration: '6 weeks', type: 'Enterprise' },
    { service: 'Office Recabling', client: 'MedCore Japan', location: 'Osaka', duration: '3 weeks', type: 'Medium' },
    { service: 'Switch & Firewall', client: 'Finbridge Asia', location: 'Tokyo', duration: '4 weeks', type: 'High' },
    { service: 'Conferencing Setup', client: 'EduNet Corp', location: 'Nagoya', duration: '2 weeks', type: 'Medium' },
  ];

  // Animate stats on mount
  useEffect(() => {
    stats.forEach((stat, index) => {
      if (typeof stat.value !== 'number') {
        return;
      }

      setTimeout(() => {
        let current = 0;
        const increment = stat.value / 50;
        const timer = setInterval(() => {
          current += increment;
          if (current >= stat.value) {
            current = stat.value;
            clearInterval(timer);
          }
          setAnimatedStats(prev => ({
            ...prev,
            [index]: current.toFixed(0)
          }));
        }, 30);
      }, index * 200);
    });
  }, []);

  const ServiceModal = ({ service, onClose }) => (
    <AnimatePresence>
      {service && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            className="bg-white rounded-2xl max-w-2xl w-full p-8 relative max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full">
              <X className="h-6 w-6" />
            </button>
            
            <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-r ${service.color || 'from-blue-500 to-cyan-500'} mb-6`}>
              <service.icon className="h-8 w-8 text-white" />
            </div>
            
            <h2 className="text-3xl font-bold mb-4">{service.title}</h2>
            <p className="text-gray-600 mb-6 text-lg">{service.description}</p>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-xl">
                <div className="text-2xl font-bold text-blue-600">{service.stats.timeline}</div>
                <div className="text-sm text-gray-600">Typical Timeline</div>
              </div>
              <div className="bg-green-50 p-4 rounded-xl">
                <div className="text-2xl font-bold text-green-600">{service.stats.complexity}</div>
                <div className="text-sm text-gray-600">Complexity Level</div>
              </div>
            </div>
            
            <div className="mb-6">
              <h3 className="font-bold mb-3">Key Highlights</h3>
              <div className="flex flex-wrap gap-2">
                {service.highlights.map((h, i) => (
                  <span key={i} className="px-4 py-2 bg-blue-100 text-blue-700 rounded-full font-medium">
                    {h}
                  </span>
                ))}
              </div>
            </div>
            
            <Link to={`/services/${service.slug}`}>
              <Button className="w-full bg-gradient-to-r from-blue-600 to-cyan-500">
                Learn More About {service.title}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <>
      <Helmet>
        <title>Tasks IT Services - Enterprise IT Infrastructure Solutions</title>
        <meta name="description" content="Tasks IT Services delivers end-to-end IT infrastructure solutions: data center migration, office recabling, switch & firewall, AV systems, conferencing, and IT warehousing." />
      </Helmet>

      {/* Enhanced Hero Section with Carousel */}
      <section className="relative h-screen overflow-hidden">
        <Swiper
          modules={[Autoplay, Pagination, Navigation, EffectFade]}
          effect="fade"
          autoplay={{ delay: 5000, disableOnInteraction: false }}
          pagination={{ clickable: true }}
          navigation
          onSlideChange={(swiper) => setCurrentSlide(swiper.activeIndex)}
          className="h-full"
        >
          {heroSlides.map((slide, index) => (
            <SwiperSlide key={index}>
              <div className="relative h-full">
                {/* Background Image with Overlay */}
                <div className="absolute inset-0">
                  <img 
                    src={slide.image} 
                    alt={slide.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-900/95 via-blue-800/90 to-cyan-700/85"></div>
                  <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:32px_32px]"></div>
                </div>

                {/* Content */}
                <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
                  <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="max-w-3xl text-white"
                  >
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.4 }}
                      className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6 border border-white/20"
                    >
                      <Sparkles className="h-4 w-4 text-yellow-300" />
                      <span className="text-sm font-medium">{slide.highlight}</span>
                    </motion.div>

                    <motion.h1
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                      className="text-6xl md:text-7xl font-bold mb-6 leading-tight"
                    >
                      {slide.title}
                    </motion.h1>

                    <motion.p
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6 }}
                      className="text-2xl text-blue-100 mb-8 leading-relaxed"
                    >
                      {slide.subtitle}
                    </motion.p>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.7 }}
                      className="flex flex-wrap gap-4"
                    >
                      <Link to="/contact">
                        <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 shadow-2xl hover:shadow-3xl transition-all text-lg px-8 py-6 group">
                          {slide.cta}
                          <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </Link>
                    </motion.div>


                  </motion.div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </section>

      {/* Animated Stats Section */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-cyan-50/50"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05, y: -5 }}
                className="text-center bg-white p-6 rounded-2xl shadow-lg hover:shadow-2xl transition-all cursor-pointer group"
              >
                <motion.div
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                  className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-r ${stat.color} mb-4 shadow-lg group-hover:shadow-xl`}
                >
                  <stat.icon className="h-8 w-8 text-white" />
                </motion.div>
                <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-2 leading-tight break-words whitespace-normal">
                  {typeof stat.value === 'number' ? `${animatedStats[index] || 0}${stat.suffix}` : stat.suffix}
                </div>
                <div className="text-sm md:text-base text-gray-800 font-semibold mb-1 leading-tight break-words whitespace-normal">{stat.label}</div>
                <div className="text-sm md:text-base text-gray-500 leading-relaxed">{stat.description}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl font-bold mb-4">
              Our <span className="text-blue-600">IT Services</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From network infrastructure to AV systems, we deliver tailored IT solutions that keep your business running. Click a service to learn more.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                onClick={() => setSelectedService(service)}
                className="group bg-white border-2 border-gray-100 p-6 rounded-2xl hover:border-blue-300 hover:shadow-2xl transition-all cursor-pointer relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform opacity-0 group-hover:opacity-100"></div>
                
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  className={`relative inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-r ${service.color} mb-4 shadow-lg`}
                >
                  <service.icon className="h-7 w-7 text-white" />
                </motion.div>
                
                <h3 className="relative text-lg md:text-xl font-bold mb-3 group-hover:text-blue-600 transition-colors leading-tight">{service.title}</h3>
                <p className="relative text-gray-600 mb-4 text-sm md:text-base leading-relaxed line-clamp-3 break-words">{service.description}</p>
                
                <div className="relative flex flex-wrap gap-2 mb-4">
                  {service.highlights.slice(0, 2).map((h, i) => (
                    <span key={i} className="text-xs px-3 py-1 bg-blue-50 text-blue-700 rounded-full font-medium">
                      {h}
                    </span>
                  ))}
                </div>
                
                <div className="relative text-sm text-blue-600 font-semibold group-hover:translate-x-2 transition-transform inline-flex items-center">
                  Learn more <ArrowRight className="ml-1 h-4 w-4" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <ServiceModal service={selectedService} onClose={() => setSelectedService(null)} />

      {/* How It Works with Timeline */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-cyan-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:32px_32px]"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl font-bold mb-4">How We Work</h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Our streamlined 4-step process ensures you get the right talent, fast.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="relative bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20 hover:bg-white/20 transition-all"
              >
                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-white/30">
                    <ChevronRight className="absolute -right-2 -top-2 h-5 w-5 text-white/50" />
                  </div>
                )}
                
                <div className="relative">
                  <div className="absolute -top-4 -left-2 w-10 h-10 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full flex items-center justify-center text-white font-bold shadow-lg text-base md:text-lg">
                    {index + 1}
                  </div>
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-white/20 mb-4 mt-6">
                    <step.icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-lg md:text-xl font-bold mb-3 leading-tight">{step.title}</h3>
                  <p className="text-blue-100 text-sm md:text-base leading-relaxed">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries with Hover Effects */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl font-bold mb-4">
              Industries We <span className="text-blue-600">Serve</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Delivering enterprise IT solutions across diverse sectors
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ scale: 1.1, rotate: 2 }}
                className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-2xl text-center hover:shadow-xl transition-all cursor-pointer group relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 to-cyan-500/0 group-hover:from-blue-500/10 group-hover:to-cyan-500/10 transition-all"></div>
                <industry.icon className="h-10 w-10 text-blue-600 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-bold text-gray-900 mb-1 text-base md:text-lg leading-tight">{industry.name}</h3>
                <p className="text-sm md:text-base text-blue-600 font-semibold mb-1">{industry.clients}</p>
                <p className="text-xs md:text-sm text-gray-500 leading-snug">{industry.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

    

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-5xl font-bold mb-6">Why Companies Choose Us</h2>
              <p className="text-gray-600 text-lg mb-8">
                We deliver enterprise IT infrastructure with precision, speed, and full documentation — so you can focus on your business, not your IT.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ x: 10 }}
                    className="flex items-center gap-4 p-4 rounded-xl hover:bg-blue-50 transition-all cursor-pointer group"
                  >
                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                      <benefit.icon className="h-6 w-6 text-white" />
                    </div>
                    <span className="text-gray-700 font-medium text-lg">{benefit.text}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-3xl blur-3xl opacity-20"></div>
              <img
                src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?q=80&w=2070"
                alt="Network infrastructure"
                className="relative rounded-2xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>


      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-blue-600 via-blue-700 to-cyan-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:32px_32px]"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            >
              <Target className="h-20 w-20 mx-auto mb-6 text-blue-200" />
            </motion.div>
            <h2 className="text-5xl md:text-6xl font-bold mb-6">
              Ready to have a reliable partner you can count on? 
            </h2>
            <p className="text-2xl text-blue-100 mb-10 max-w-2xl mx-auto leading-relaxed">
              Let's discuss your project needs and build a solution that works for your business. Get a free consultation today.
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 shadow-2xl text-xl px-10 py-7 group">
                  Get started in less than 5 minutes
                  <ArrowRight className="ml-2 h-6 w-6 group-hover:translate-x-2 transition-transform" />
                </Button>
              </Link>
              <Link to="/services">
                <Button size="lg" className="bg-blue-500 text-white border-2 border-white hover:bg-blue-600 shadow-2xl text-xl px-10 py-7">
                  View Our Services
                </Button>
              </Link>
            </div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="mt-12 flex flex-wrap justify-center gap-8 text-blue-100"
            >
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                <span>Transparency</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                <span>Response within 24 hours</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                <span>Tailored project scoping</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Video Modal */}
      <AnimatePresence>
        {showVideoModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowVideoModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="relative max-w-4xl w-full bg-black rounded-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowVideoModal(false)}
                className="absolute top-4 right-4 z-10 p-2 bg-white/10 hover:bg-white/20 rounded-full backdrop-blur-sm"
              >
                <X className="h-6 w-6 text-white" />
              </button>
             <div className="aspect-video">
                <video
                  className="w-full h-full"
                  src={demoVideo}
                  controls
                  autoPlay
                  muted
                  playsInline
                  preload="metadata"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Home;